"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { GrowthIndicator } from "../GrowthIndicator";

export interface EarningsSummaryCardProps {
  title: string;
  amount: string;
  delta?: string;
  deltaLabel?: string;
  growthValue?: number; // Add growth value for new GrowthIndicator
  isPositive?: boolean; // Manual override for positive/negative growth
  renderDelta?: React.ReactNode; // Custom component to render instead of default delta/growth
  deltaColor?: string; // Custom color for delta text and indicator
  deltaBgColor?: string; // Custom background color for delta label
  tone?: "pink" | "green";
  className?: string;
}

export function EarningsSummaryCard({
  title,
  amount,
  delta,
  deltaLabel,
  growthValue,
  isPositive,
  renderDelta,
  deltaColor: customDeltaColor,
  deltaBgColor: customDeltaBgColor,
  tone = "pink",
  className,
}: EarningsSummaryCardProps) {
  const toneColor = tone === "pink" ? "#ED5AA9" : "#2EB67D";

  // Determine if delta is positive or negative for color
  const isPositiveGrowth = isPositive ?? (delta ? (delta.startsWith('+') || (!delta.startsWith('-') && delta !== '0' && delta !== '+0%')) : true);
  const deltaColor = customDeltaColor ?? (isPositiveGrowth ? '#2EB67D' : '#EF4444');
  const deltaBgColor = customDeltaBgColor ?? (isPositiveGrowth ? '#E3F6ED' : '#FEE2E2');

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl border border-black bg-white px-4 py-3 shadow-[0_4px_0_0_#000000]",
        className
      )}
    >
      <p className="text-sm font-medium text-[#344054]">{title}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-bold" style={{ color: toneColor }}>
          {amount}
        </p>
        
        {/* Use custom render if provided */}
        {renderDelta ? (
          renderDelta
        ) : typeof growthValue === 'number' ? (
          <GrowthIndicator value={growthValue} size="sm" />
        ) : delta ? (
          <div className="flex items-center gap-1 text-xs font-medium" style={{ color: deltaColor }}>
            <span>{delta}</span>
            {deltaLabel && (
              <span
                className="inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px]"
                style={{ backgroundColor: deltaBgColor, color: deltaColor }}
              >
                {deltaLabel}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
